/* ==========================================================================
   Cart Page V2 — quantity + remove + add-to-cart logic for
   sections/main-cart-v2.liquid

   Every cart mutation goes through an AJAX call FIRST (so Discount Ninja's
   interceptor observes the change and re-evaluates its promotions) and is
   followed by a FULL page reload (so the whole page — theme rows, summary
   and DN's dynamic pricing — re-renders consistently). No client-side
   section re-rendering: that wiped DN's DOM work and caused the
   "discount sometimes missing" flakiness.

   - Steppers respect MOQ + multiples (custom.moq / custom.multiple_of,
     server-rendered as data attributes; off-grid quantities snap to valid).
   - Error responses (e.g. max stock) show a message and skip the reload
     (the cart didn't change).
   ========================================================================== */

(function () {
  'use strict';

  var busy = false;

  function onCartPage() {
    return !!document.getElementById('CartPageV2');
  }

  function routes() {
    var r = (window.theme && window.theme.routes) || {};
    return {
      cartAdd: r.cartAdd || '/cart/add.js',
      cartChange: r.cartChange || '/cart/change.js'
    };
  }

  /*= Add to cart ("You may also like" + its quick-view modal) =============*/

  document.addEventListener('submit', function (evt) {
    if (!onCartPage()) return;
    var form = evt.target;
    if (!form.querySelector || !form.querySelector('.js-add-to-cart')) return;
    evt.preventDefault();

    if (busy) return;
    busy = true;

    var button = form.querySelector('.js-add-to-cart');
    if (button) button.classList.add('btn--loading');

    fetch(routes().cartAdd, {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        if (data && (data.status === 422 || data.status === 404)) {
          busy = false;
          if (button) button.classList.remove('btn--loading');
          showError(data.description || data.message || '');
          return;
        }
        window.location.reload();
      })
      .catch(function () {
        busy = false;
        if (button) button.classList.remove('btn--loading');
      });
  });

  /*= Quantity stepper + remove ============================================*/

  document.addEventListener('click', function (evt) {
    if (!onCartPage()) return;

    var minus = evt.target.closest('[data-cpv2-minus]');
    var plus = evt.target.closest('[data-cpv2-plus]');
    var remove = evt.target.closest('[data-cpv2-remove]');
    if (!minus && !plus && !remove) return;

    var row = evt.target.closest('[data-cpv2-item]');
    if (!row) return;

    var key = row.getAttribute('data-key');
    var qty = parseInt(row.getAttribute('data-qty'), 10) || 0;
    var step = parseInt(row.getAttribute('data-multiple'), 10) || 1;
    var min = parseInt(row.getAttribute('data-moq'), 10) || step;

    if (remove) {
      changeItem(key, 0);
    } else if (plus) {
      changeItem(key, nextQty(qty, min, step));
    } else if (minus) {
      var down = prevQty(qty, min, step);
      if (down < qty) changeItem(key, down);
    }
  });

  // Next valid quantity above qty (valid = min + k * step; off-grid snaps up)
  function nextQty(qty, min, step) {
    if (qty < min) return min;
    return min + (Math.floor((qty - min) / step) + 1) * step;
  }

  // Previous valid quantity below qty, never below min (9 → 8 → 4 / min 4 step 4)
  function prevQty(qty, min, step) {
    if (qty <= min) return min;
    var offset = (qty - min) % step;
    var down = offset === 0 ? qty - step : qty - offset;
    return Math.max(down, min);
  }

  function changeItem(key, quantity) {
    if (busy) return;
    busy = true;

    var itemsEl = document.querySelector('[data-cpv2-items]');
    if (itemsEl) itemsEl.classList.add('is-busy');

    fetch(routes().cartChange, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        if (data && Array.isArray(data.items)) {
          window.location.reload();
        } else {
          // Error payload (e.g. exceeding available stock) — cart unchanged,
          // so no reload; just surface the message.
          busy = false;
          if (itemsEl) itemsEl.classList.remove('is-busy');
          showError(data && data.description);
        }
      })
      .catch(function () {
        busy = false;
        if (itemsEl) itemsEl.classList.remove('is-busy');
      });
  }

  /*= Discount Ninja summary sync ==========================================*/
  // DN promo discounts are NOT in Shopify cart data (checkout-only), so the
  // Liquid-rendered "Your savings" / "Estimated total" can't include them.
  // DN reliably injects its per-line discount into [data-la-dn-cart-item-text]
  // on every load — so we read those amounts and adjust the summary ourselves,
  // instead of depending on DN's flaky heuristic rewriting of our rows.

  function dnLineCents(el) {
    // Prefer a cents-bearing data attribute on DN's injected money span
    var all = el.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var attrs = all[i].attributes;
      for (var j = 0; j < attrs.length; j++) {
        if (/price-?cents?$/i.test(attrs[j].name)) {
          var v = parseInt(attrs[j].value, 10);
          if (v > 0) return v;
        }
      }
    }
    // Fallback: parse the money amount out of the text (anchored on CHF)
    var txt = (el.textContent || '').replace(/[’'’]/g, '');
    var m = txt.match(/CHF\s*([\d,\.]+)/i) || txt.match(/([\d]+(?:[.,]\d{1,2})?)\s*(?:CHF|Fr)/i);
    if (!m) return 0;
    var num = parseFloat(m[1].replace(/,(?=\d{3}\b)/g, '').replace(',', '.'));
    return isNaN(num) ? 0 : Math.round(num * 100);
  }

  function dnTotalCents() {
    var sum = 0;
    document.querySelectorAll('[data-la-dn-cart-item-text]').forEach(function (el) {
      sum += dnLineCents(el);
    });
    return sum;
  }

  function fmtMoney(cents) {
    if (window.theme && window.theme.Currency && window.theme.settings) {
      return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
    }
    return 'CHF ' + (cents / 100).toFixed(2);
  }

  // Base values captured ONCE at load, before DN can touch the DOM. They
  // never change within a page lifetime (every cart mutation full-reloads).
  var summaryBase = null;

  function captureBases() {
    var s = document.querySelector('[data-cpv2-savings]');
    var t = document.querySelector('[data-cpv2-total]');
    summaryBase = {
      saving: s ? parseInt(s.getAttribute('data-base'), 10) || 0 : 0,
      total: t ? parseInt(t.getAttribute('data-base'), 10) || 0 : 0,
      // Discounts ALREADY in Shopify cart data at render time. DN's automatic
      // rule applies a real Shopify discount with a backend delay — when it
      // has landed, the Liquid base already includes it and there is nothing
      // to top up (adding again was the cause of the doubling).
      cartDiscount: s ? parseInt(s.getAttribute('data-cart-discount'), 10) || 0 : 0
    };
  }

  // Format-agnostic: read the money amount currently DISPLAYED in a row
  // (works whether it was rendered by Liquid, by us, or rewritten by DN).
  function parseMoneyCents(text) {
    if (!text) return null;
    var t = String(text).replace(/[’']/g, '').replace(/[−–]/g, '-');
    var m = t.match(/CHF\s*([\d.,]+)/i);
    if (!m) return null;
    var v = parseFloat(m[1].replace(/,(?=\d{3}(\D|$))/g, ''));
    return isNaN(v) ? null : Math.round(v * 100);
  }

  // Single-writer rule: only touch a summary row if it still shows the BASE
  // value (i.e. DN's own summary rewrite did NOT run). If the displayed value
  // already includes DN's discount — or anything else — leave it alone, so we
  // never trigger DN into re-adding on top (the cause of the doubling).
  function applyIfDnMissing(rowAttr, spanAttr, base, expected, prefix) {
    var row = document.querySelector('[' + rowAttr + ']');
    if (!row || row.__cpv2Pending) return;

    var cur = parseMoneyCents(row.textContent);
    if (cur === null) cur = base;
    if (cur !== base || expected === base) return; // DN applied, or nothing to add

    // Confirm after a short delay — DN's own summary pass may land a moment
    // after its line text; if it does, we back off.
    row.__cpv2Pending = true;
    window.setTimeout(function () {
      row.__cpv2Pending = false;
      var cur2 = parseMoneyCents(row.textContent);
      if (cur2 !== null && cur2 !== base) return; // DN landed meanwhile

      var el = row.querySelector('[' + spanAttr + ']');
      if (!el) {
        var spans = row.querySelectorAll('span');
        el = spans.length > 1 ? spans[spans.length - 1] : null;
      }
      if (!el) return;
      el.textContent = prefix + fmtMoney(expected);
      if (expected > 0) row.hidden = false;
    }, 700);
  }

  // Hide the "Your savings" row whenever its displayed value is zero — the
  // Liquid hides it at 0, but DN can un-hide and write "−CHF 0.00" into it.
  function hideZeroSavings() {
    var row = document.querySelector('[data-cpv2-savings-row]');
    if (!row) return;
    var cents = parseMoneyCents(row.textContent);
    if (cents !== null && cents <= 0 && !row.hidden) row.hidden = true;
  }

  function syncDnSummary() {
    if (!summaryBase) return;

    hideZeroSavings();

    // Only top up the part of DN's discount NOT already in the cart data.
    // DN promo fully applied server-side → topup 0 → never touch the summary.
    var topup = dnTotalCents() - summaryBase.cartDiscount;
    if (topup <= 0) return;

    applyIfDnMissing('data-cpv2-savings-row', 'data-cpv2-savings', summaryBase.saving, summaryBase.saving + topup, '−');
    applyIfDnMissing('data-cpv2-total-row', 'data-cpv2-total', summaryBase.total, summaryBase.total - topup, '');
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (!onCartPage()) return;

    captureBases();

    // Watch for DN's line-text injection (reliable on every load) — once its
    // amount is known, top up the summary ONLY where DN itself didn't.
    var container = document.getElementById('CartPageV2');
    if (container && window.MutationObserver) {
      var observer = new MutationObserver(function () {
        window.requestAnimationFrame(syncDnSummary);
      });
      observer.observe(container, { childList: true, subtree: true, characterData: true });
    }

    // Fallback checks in case DN lands without observable mutations
    window.setTimeout(syncDnSummary, 1200);
    window.setTimeout(syncDnSummary, 3000);
    window.setTimeout(syncDnSummary, 6000);
  });

  /*= Error display =========================================================*/

  var errorTimer = null;

  function showError(message) {
    if (!message) return;
    var errorEl = document.querySelector('[data-cpv2-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
    if (errorTimer) window.clearTimeout(errorTimer);
    errorTimer = window.setTimeout(function () {
      errorEl.hidden = true;
    }, 6000);
  }
})();
